import React from "react";
import { Box } from "@mui/material";
import {
  Stage,
  Layer,
  Group,
  Circle,
  Rect,
  Transformer,
  Image as KonvaImage,
} from "react-konva";
import { GAUGES } from "./render";
import SyncBar from "./SyncBar";

const DESIGN_WIDTH = 1080;
const DESIGN_HEIGHT = 1920;
const FPS = 25;

function useImage(src) {
  const [img, setImg] = React.useState(null);
  React.useEffect(() => {
    if (!src) return;
    const image = new window.Image();
    image.onload = () => setImg(image);
    image.src = src;
  }, [src]);
  return src ? img : null;
}

const Gauge = ({ shapeProps, source, isSelected, onSelect, onChange, touch, scale }) => {
  const shapeRef = React.useRef();
  const trRef = React.useRef();
  // Anchors live inside the scaled layer, so divide by the stage scale to keep
  // a constant on-screen handle size — big enough to grab with a fingertip.
  const anchorSize = Math.round((touch ? 26 : 11) / (scale || 1));
  const fileImg = useImage(source ? null : shapeProps.img);
  const img = source || fileImg;

  React.useEffect(() => {
    if (isSelected) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const { enabled, name, ...konvaProps } = shapeProps;

  return (
    <React.Fragment>
      <KonvaImage
        image={img}
        onClick={onSelect}
        onTap={onSelect}
        ref={shapeRef}
        {...konvaProps}
        draggable
        onDragEnd={(e) => {
          onChange({
            ...shapeProps,
            x: e.target.x(),
            y: e.target.y(),
          });
        }}
        onTransformEnd={() => {
          const node = shapeRef.current;
          const scale = node.scaleX();
          node.scaleX(1);
          node.scaleY(1);

          onChange({
            ...shapeProps,
            x: node.x(),
            y: node.y(),
            width: Math.max(5, node.width() * scale),
            height: Math.max(5, node.height() * scale),
            scale: (shapeProps.scale || 1) * scale,
          });
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          rotateEnabled={false}
          centeredScaling
          anchorStroke="#e30613"
          anchorFill="#ffffff"
          anchorSize={anchorSize}
          anchorCornerRadius={Math.round(anchorSize / 4)}
          borderStroke="#e30613"
          borderStrokeWidth={Math.max(1.5, 1.5 / (scale || 1))}
          enabledAnchors={[
            "top-left",
            "top-right",
            "bottom-left",
            "bottom-right",
          ]}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 5 || newBox.height < 5) {
              return oldBox;
            }
            return newBox;
          }}
        />
      )}
    </React.Fragment>
  );
};

// Placeholder shapes roughly where the Instagram Reels UI sits, so gauges can
// be kept out of the covered areas. Preview-only — never rendered to video.
const ReelsSkeleton = () => {
  const bar = "rgba(255,255,255,0.45)";
  const faint = "rgba(255,255,255,0.25)";
  const railX = 1000;
  return (
    <Group listening={false}>
      {/* top bar: "Reels" title + camera button */}
      <Rect x={44} y={70} width={190} height={52} cornerRadius={12} fill={bar} />
      <Circle x={1000} y={96} radius={34} stroke={bar} strokeWidth={6} />

      {/* right action rail: like / comment / share / more + counts */}
      {[1130, 1290, 1450].map((y) => (
        <Group key={y}>
          <Circle x={railX} y={y} radius={40} stroke={bar} strokeWidth={7} />
          <Rect
            x={railX - 35}
            y={y + 58}
            width={70}
            height={22}
            cornerRadius={11}
            fill={faint}
          />
        </Group>
      ))}
      <Circle x={railX} y={1590} radius={14} fill={bar} />
      <Circle x={railX - 38} y={1590} radius={14} fill={bar} />
      <Circle x={railX + 38} y={1590} radius={14} fill={bar} />
      {/* rotating album art */}
      <Rect
        x={railX - 38}
        y={1660}
        width={76}
        height={76}
        cornerRadius={16}
        stroke={bar}
        strokeWidth={6}
      />

      {/* bottom-left: avatar + username, caption lines, music pill */}
      <Circle x={90} y={1620} radius={40} fill={faint} stroke={bar} strokeWidth={5} />
      <Rect x={150} y={1600} width={240} height={36} cornerRadius={10} fill={bar} />
      <Rect x={430} y={1602} width={150} height={32} cornerRadius={16} stroke={bar} strokeWidth={5} />
      <Rect x={50} y={1690} width={620} height={26} cornerRadius={13} fill={faint} />
      <Rect x={50} y={1730} width={460} height={26} cornerRadius={13} fill={faint} />
      <Rect x={50} y={1790} width={330} height={44} cornerRadius={22} fill={faint} />

      {/* home indicator */}
      <Rect x={390} y={1886} width={300} height={10} cornerRadius={5} fill={bar} />
    </Group>
  );
};

const Canvas = ({
  rectangles,
  setRectangles,
  showReelsUI,
  videoEl,
  rows,
  columns,
  offset,
  setOffset,
  isMobile,
}) => {
  const [selectedId, selectShape] = React.useState(null);
  const containerRef = React.useRef(null);
  const layerRef = React.useRef(null);
  const [scale, setScale] = React.useState(0.3);
  const background = useImage(videoEl ? null : "/overlay-bg.png");

  const live = videoEl && rows ? { rows, columns, offset } : null;
  const liveRef = React.useRef();
  liveRef.current = { live, rectangles };

  const scratches = React.useRef({});
  const getScratch = (id) => {
    if (!scratches.current[id]) {
      const g = GAUGES[id];
      const c = document.createElement("canvas");
      c.width = g.width;
      c.height = g.height;
      g.draw(c.getContext("2d"), g.sample);
      scratches.current[id] = c;
    }
    return scratches.current[id];
  };

  React.useEffect(() => {
    const update = () => {
      const el = containerRef.current;
      if (!el) return;
      // Tighter margins on phones so the tall 9:16 preview uses the screen;
      // the sync bar's transport controls wrap to more rows on narrow widths.
      const padding = isMobile ? 16 : 48;
      const barSpace = videoEl ? (isMobile ? 168 : 130) : 0;
      setScale(
        Math.max(
          0.01,
          Math.min(
            (el.clientWidth - padding) / DESIGN_WIDTH,
            (el.clientHeight - padding - barSpace) / DESIGN_HEIGHT
          )
        )
      );
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [videoEl, isMobile]);

  // Live preview loop: repaint the video frame and drive the gauge painters
  // with the log row at the current (offset-shifted) time.
  React.useEffect(() => {
    if (!videoEl) return;
    let raf;
    const tick = () => {
      const { live: l, rectangles: rects } = liveRef.current;
      if (l) {
        const idx = Math.min(
          Math.max(Math.round((videoEl.currentTime - l.offset) * FPS), 0),
          l.rows.length - 1
        );
        for (const r of rects) {
          if (!r.enabled || l.columns[r.id] === -1) continue;
          const gauge = GAUGES[r.id];
          const scratch = getScratch(r.id);
          const ctx = scratch.getContext("2d");
          ctx.clearRect(0, 0, scratch.width, scratch.height);
          gauge.draw(ctx, l.rows[idx][l.columns[r.id]]);
        }
      }
      layerRef.current?.batchDraw();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [videoEl]);

  const checkDeselect = (e) => {
    // deselect when clicking the stage or the background image
    if (e.target === e.target.getStage() || e.target.name() === "background") {
      selectShape(null);
    }
  };

  const videoFit = videoEl
    ? (() => {
        const s = Math.max(
          DESIGN_WIDTH / videoEl.videoWidth,
          DESIGN_HEIGHT / videoEl.videoHeight
        );
        return {
          x: (DESIGN_WIDTH - videoEl.videoWidth * s) / 2,
          y: (DESIGN_HEIGHT - videoEl.videoHeight * s) / 2,
          width: videoEl.videoWidth * s,
          height: videoEl.videoHeight * s,
        };
      })()
    : null;

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        backgroundImage:
          "radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)",
        backgroundSize: "24px 24px",
      }}
    >
      <Box
        sx={{
          lineHeight: 0,
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
        }}
      >
        <Stage
          width={DESIGN_WIDTH * scale}
          height={DESIGN_HEIGHT * scale}
          scaleX={scale}
          scaleY={scale}
          onMouseDown={checkDeselect}
          onTouchStart={checkDeselect}
        >
          <Layer ref={layerRef}>
            {videoEl ? (
              <KonvaImage name="background" image={videoEl} {...videoFit} />
            ) : (
              background && (
                <KonvaImage
                  name="background"
                  image={background}
                  width={DESIGN_WIDTH}
                  height={DESIGN_HEIGHT}
                />
              )
            )}
            {rectangles.map((rect, i) => {
              if (!rect.enabled) return null;
              const isLive = live && live.columns[rect.id] !== -1;
              return (
                <Gauge
                  key={rect.id}
                  shapeProps={rect}
                  source={isLive ? getScratch(rect.id) : null}
                  isSelected={rect.id === selectedId}
                  touch={isMobile}
                  scale={scale}
                  onSelect={() => selectShape(rect.id)}
                  onChange={(newAttrs) => {
                    const rects = rectangles.slice();
                    rects[i] = newAttrs;
                    setRectangles(rects);
                  }}
                />
              );
            })}
            {showReelsUI && <ReelsSkeleton />}
          </Layer>
        </Stage>
      </Box>
      {videoEl && (
        <SyncBar
          videoEl={videoEl}
          rows={rows}
          rpmColumn={columns ? columns.tachometer : -1}
          offset={offset}
          setOffset={setOffset}
          width={Math.round(DESIGN_WIDTH * scale)}
          isMobile={isMobile}
        />
      )}
    </Box>
  );
};

export default Canvas;
